import { useState } from "react";
import { Navigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

const LOGO = "https://framerusercontent.com/images/kXzfcOIGDr33MzqF8NWQisPPw.png?width=677&height=832";
const VILLAGE = "https://framerusercontent.com/images/GY9CeT1LtUTxJFqxBRiA5hGPVI.png?width=3600&height=1740";
const LEAF_A = "https://framerusercontent.com/images/7SZnAXEXUa0r8TE8uJThPBzWbyw.png?width=330&height=339";
const LEAF_B = "https://framerusercontent.com/images/5bAt7EHQdFT79UKnBOMY68uREnk.png?width=202&height=226";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/overview" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try { await login(email, password); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden">
      <img src={LEAF_A} alt="" className="pointer-events-none absolute -right-10 -top-10 w-72 opacity-60" />
      <img src={LEAF_B} alt="" className="pointer-events-none absolute -bottom-10 -left-6 w-56 opacity-60" />
      <header className="relative flex items-center gap-3 px-6 py-5 sm:px-10">
        <img src={LOGO} alt="Khanata" className="h-14 w-auto" />
        <span className="font-display text-2xl text-[hsl(var(--brown))] sm:text-3xl">Khanata Digital Designs Inc.</span>
      </header>
      <div className="relative mx-auto grid w-full max-w-6xl flex-1 items-center gap-12 px-6 pb-20 lg:grid-cols-2 lg:gap-20">
        <div className="kh-rise hidden lg:block" data-testid="login-hero">
          <div role="img" aria-label="Khanata virtual design village" className="h-[420px] w-[400px] bg-no-repeat mix-blend-multiply"
            style={{ backgroundImage: `url(${VILLAGE})`, backgroundSize: "1180px auto", backgroundPosition: "50% 30%" }} />
          <h1 className="mt-6 font-display text-5xl text-foreground lg:text-6xl">Rooted In Outreach</h1>
          <p className="mt-4 max-w-md text-muted-foreground">Track-based research, evidence-backed qualification and Outlook drafts you review before anything is sent.</p>
        </div>
        <form onSubmit={submit} className="kh-card kh-rise w-full max-w-md justify-self-center space-y-6 p-8 sm:p-10 lg:justify-self-end" data-testid="login-form">
          <div>
            <div className="kh-eyebrow">Private portal</div>
            <h2 className="mt-2 font-display text-4xl">Welcome back</h2>
            <p className="mt-1 text-sm text-muted-foreground">portal.khanata.com</p>
          </div>
          <div className="space-y-4">
            <label className="block space-y-1.5"><span className="text-sm font-medium">Email</span>
              <Input data-testid="login-email-input" type="email" placeholder="name@khanata.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11 bg-background/60" /></label>
            <label className="block space-y-1.5"><span className="text-sm font-medium">Password</span>
              <Input data-testid="login-password-input" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11 bg-background/60" /></label>
          </div>
          {err && <div data-testid="login-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700">{err}</div>}
          <button data-testid="login-submit-button" disabled={busy}
            className="group flex h-12 w-full items-center justify-center gap-2 rounded-[10px] bg-primary font-medium text-primary-foreground transition-[opacity,transform] hover:opacity-90 active:scale-[0.98] disabled:opacity-60">
            {busy ? "Signing in…" : "Sign in"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </form>
      </div>
    </div>
  );
}
